import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import Modal from '../ui/Modal';
import { GoogleSheetsIcon } from './GoogleServiceIcons';
import { PlusIcon, ExternalLinkIcon, CopyIcon, CheckIcon, AlertCircleIcon } from '../icons/uiIcons';
import {
  googleSheetsList,
  googleSheetCreate,
  googleSheetAddExisting,
  parseSheetId,
  type SheetItem,
  type GoogleAccountDoc,
} from '../../lib/google';

interface GoogleSheetsManagerProps {
  accounts: GoogleAccountDoc[];
  selectedAccountId: string | null;
  onSelectAccount: (accountId: string) => void;
}

export function GoogleSheetsManager({
  accounts,
  selectedAccountId,
  onSelectAccount,
}: GoogleSheetsManagerProps) {
  const { t } = useI18n();

  const [sheets, setSheets] = useState<SheetItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [existingUrlOrId, setExistingUrlOrId] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Copy feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchSheets = async (accId: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await googleSheetsList(accId);
      setSheets(res.sheets || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load spreadsheets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAccountId) {
      fetchSheets(selectedAccountId);
    } else {
      setSheets([]);
    }
  }, [selectedAccountId]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCreateSheet = async () => {
    if (!selectedAccountId || !newTitle.trim()) return;
    try {
      setCreating(true);
      setCreateError(null);
      await googleSheetCreate(selectedAccountId, newTitle.trim());
      setCreateModalOpen(false);
      setNewTitle('');
      await fetchSheets(selectedAccountId);
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create spreadsheet');
    } finally {
      setCreating(false);
    }
  };

  const handleAddExisting = async () => {
    if (!selectedAccountId || !existingUrlOrId.trim()) return;
    const cleanId = parseSheetId(existingUrlOrId);
    if (!cleanId) {
      setAddError(
        t('google.sheets.invalidUrlOrId') ||
          'Please enter a valid Google Spreadsheet URL or 15+ character Sheet ID.',
      );
      return;
    }

    try {
      setAdding(true);
      setAddError(null);
      await googleSheetAddExisting(selectedAccountId, existingUrlOrId.trim());
      setAddModalOpen(false);
      setExistingUrlOrId('');
      await fetchSheets(selectedAccountId);
    } catch (err: any) {
      setAddError(err.message || 'Failed to link spreadsheet. Ensure the Google account has read/write access.');
    } finally {
      setAdding(false);
    }
  };

  if (accounts.length === 0) {
    return null;
  }

  return (
    <Card className="p-4 sm:p-6 border border-line bg-white shadow-card dark:border-slate-800 dark:bg-slate-900">
      {/* Header with Account Selector and Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900 shrink-0">
            <GoogleSheetsIcon size={22} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-ink dark:text-slate-100">
              {t('google.sheets.sectionTitle') || 'Google Spreadsheets'}
            </h3>
            <p className="text-xs text-ink-3 dark:text-slate-400 mt-0.5">
              {t('google.sheets.sectionSubtitle') ||
                'Tracked subscriber sheets and spreadsheets discovered in your Google Drive.'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Account picker */}
          {accounts.length > 1 && (
            <select
              value={selectedAccountId || ''}
              onChange={(e) => onSelectAccount(e.target.value)}
              className="h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-ink-2 outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.email}
                </option>
              ))}
            </select>
          )}

          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setAddModalOpen(true);
              setAddError(null);
            }}
          >
            {t('google.sheets.addExistingBtn') || 'Add Existing Sheet'}
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setCreateModalOpen(true);
              setCreateError(null);
            }}
            leadingIcon={<PlusIcon size={14} />}
          >
            {t('google.sheets.createNewBtn') || 'Create New Sheet'}
          </Button>
        </div>
      </div>

      {/* Sheets Content */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs flex items-center gap-2">
          <AlertCircleIcon size={16} className="shrink-0 text-red-500" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="py-12 flex flex-col items-center justify-center text-ink-3 dark:text-slate-400 gap-2">
          <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-xs">{t('common.loading') || 'Loading spreadsheets...'}</span>
        </div>
      ) : sheets.length === 0 ? (
        <div className="py-10 text-center space-y-2">
          <p className="text-xs text-ink-3 dark:text-slate-400">
            {t('google.sheets.empty') ||
              'No spreadsheets tracked yet. Create a new sheet or link an existing one to automate subscriber logging.'}
          </p>
          <div className="pt-2 flex justify-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCreateModalOpen(true)}
              leadingIcon={<PlusIcon size={14} />}
            >
              {t('google.sheets.createNewBtn') || 'Create New Sheet'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-start text-xs border-collapse">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-800 text-ink-3 dark:text-slate-400 font-semibold">
                <th className="pb-3 text-start">{t('google.sheets.colTitle') || 'Spreadsheet Title'}</th>
                <th className="pb-3 text-start">{t('google.sheets.colId') || 'Sheet ID'}</th>
                <th className="pb-3 text-start">{t('google.sheets.colSource') || 'Status'}</th>
                <th className="pb-3 text-end">{t('google.sheets.colAction') || 'Action'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-ink dark:text-slate-200">
              {sheets.map((sheet) => (
                <tr key={sheet.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                  <td className="py-3 font-semibold flex items-center gap-2">
                    <GoogleSheetsIcon size={16} className="shrink-0" />
                    <span className="truncate max-w-xs">{sheet.title}</span>
                  </td>
                  <td className="py-3 font-mono text-[11px] text-ink-3 dark:text-slate-400">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate max-w-[140px] sm:max-w-[200px]">{sheet.id}</span>
                      <button
                        type="button"
                        onClick={() => handleCopy(sheet.id)}
                        className="text-ink-4 hover:text-ink dark:text-slate-500 dark:hover:text-slate-300"
                        title={t('common.copy') || 'Copy Sheet ID'}
                      >
                        {copiedId === sheet.id ? (
                          <CheckIcon size={12} className="text-emerald-500" />
                        ) : (
                          <CopyIcon size={12} />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="py-3">
                    <Badge variant={sheet.source === 'tracked' ? 'brand' : 'slate'} size="sm">
                      {sheet.source === 'tracked'
                        ? t('google.sheets.sourceTracked') || 'Tracked'
                        : t('google.sheets.sourceDrive') || 'In Drive'}
                    </Badge>
                  </td>
                  <td className="py-3 text-end">
                    <a
                      href={sheet.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-bold text-brand hover:text-brand-600 dark:text-brand-tint text-xs"
                    >
                      <span>{t('google.sheets.openSheet') || 'Open in Sheets'}</span>
                      <ExternalLinkIcon size={12} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: Create New Sheet */}
      {createModalOpen && (
        <Modal
          open={createModalOpen}
          onClose={() => !creating && setCreateModalOpen(false)}
          title={t('google.sheets.createModalTitle') || 'Create New Google Spreadsheet'}
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCreateModalOpen(false)}
                disabled={creating}
              >
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCreateSheet}
                disabled={creating || !newTitle.trim()}
                loading={creating}
              >
                {t('google.sheets.createBtn') || 'Create Sheet'}
              </Button>
            </div>
          }
        >
          <div className="space-y-4 text-xs">
            {createError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
                <AlertCircleIcon size={16} className="shrink-0 text-red-500" />
                <span>{createError}</span>
              </div>
            )}

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                {t('google.sheets.sheetTitleLabel') || 'Spreadsheet Title'}
              </label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={t('google.sheetNamePlaceholder') || 'e.g. WaTag WhatsApp Leads 2026'}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs outline-none focus:border-brand"
                autoFocus
              />
            </div>

            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-ink-3 dark:text-slate-400 space-y-1">
              <p className="font-semibold text-ink dark:text-slate-200">
                {t('google.sheets.headersNoteTitle') || 'Standard Header Row:'}
              </p>
              <p className="font-mono text-[11px] break-words text-brand dark:text-brand-tint">
                phone, name, email, language, country, labels, lists, createdAt
              </p>
              <p className="text-[11px] text-ink-4 dark:text-slate-500 pt-1">
                {t('google.sheets.headersNoteDesc') ||
                  'The new sheet will be initialized with subscriber columns ready for bot triggers and marketing workflows.'}
              </p>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Add Existing Sheet */}
      {addModalOpen && (
        <Modal
          open={addModalOpen}
          onClose={() => !adding && setAddModalOpen(false)}
          title={t('google.sheets.addModalTitle') || 'Add Existing Google Spreadsheet'}
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setAddModalOpen(false)}
                disabled={adding}
              >
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddExisting}
                disabled={adding || !existingUrlOrId.trim()}
                loading={adding}
              >
                {t('google.sheets.linkBtn') || 'Link Sheet'}
              </Button>
            </div>
          }
        >
          <div className="space-y-4 text-xs">
            {addError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
                <AlertCircleIcon size={16} className="shrink-0 text-red-500" />
                <span>{addError}</span>
              </div>
            )}

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                {t('google.sheets.urlOrIdLabel') || 'Google Spreadsheet URL or Sheet ID'}
              </label>
              <input
                type="text"
                value={existingUrlOrId}
                onChange={(e) => setExistingUrlOrId(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms/edit"
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-mono outline-none focus:border-brand"
                autoFocus
              />
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900 rounded-lg text-blue-800 dark:text-blue-300 text-[11px] leading-relaxed">
              <p className="font-semibold mb-0.5">
                {t('google.sheets.permissionsNoticeTitle') || 'Access Requirement:'}
              </p>
              <p>
                {t('google.sheets.permissionsNoticeDesc') ||
                  'The connected Google account must have Viewer or Editor permissions on the target sheet in Google Drive.'}
              </p>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}
